// Mapeo explícito producción ↔ Bókun Test.
//
// Reglas:
//  - En sandbox SOLO se usa `testProductId`. Nunca se cae silenciosamente al
//    identificador de producción.
//  - Un tour sin `testProductId` puede mostrarse y abrir "Ver detalles", pero
//    no puede llegar al pago ni crear reservas con otro producto sustituto.
//  - Los productos `testOnly` existen solo en Bókun Test: nunca aparecen en el
//    catálogo público ni en producción (solo detrás de ?mayasales=1&mayatest=1).

export type BokunEnv = "sandbox" | "live";

export interface BokunProductMapping {
  /** Slug público (o slug interno para productos de prueba). */
  slug: string;
  productionProductId: string | null;
  testProductId: string | null;
  testOnly?: boolean;
  label: string;
}

/** Tour técnico que ya existe en Bókun Test (no forma parte del catálogo). */
export const TEST_TOUR_SLUG = "tour-de-prueba-bokun-test-28141";

export const BOKUN_PRODUCT_MAP: readonly BokunProductMapping[] = [
  {
    slug: TEST_TOUR_SLUG,
    productionProductId: null,
    testProductId: "28141",
    testOnly: true,
    label: "Tour de prueba",
  },
  {
    slug: "tour-a-holbox-express-1237871",
    productionProductId: "1237871",
    // Copia en Bókun Test (solo sandbox). Sin fallback entre entornos.
    testProductId: "28150",
    label: "Tour a Holbox Express",
  },
  {
    // Producto técnico exclusivo de Bókun Test: varias salidas el mismo día +
    // cargo obligatorio estructurado (MXN). Nunca en catálogo ni producción.
    // Pendiente: anotar aquí su ID cuando el cliente lo entregue.
    slug: "tour-tecnico-horarios-cargos-bokun-test",
    productionProductId: null,
    testProductId: null,
    testOnly: true,
    label: "Tour técnico (horarios y cargos)",
  },
];

export const NOT_CONFIGURED_IN_TEST = "Producto no configurado en Bókun Test";

function findBySlugOrProductionId(slugOrId: string): BokunProductMapping | undefined {
  const idFromSlug = slugOrId.match(/(\d{4,})$/)?.[1] ?? null;
  return BOKUN_PRODUCT_MAP.find(
    (m) =>
      m.slug === slugOrId ||
      (m.productionProductId != null &&
        (m.productionProductId === slugOrId || m.productionProductId === idFromSlug)),
  );
}

/**
 * Devuelve el ID de Bókun a usar para reservar en el entorno dado, o null si
 * el producto no está configurado en ese entorno. Nunca sustituye un entorno
 * por otro.
 */
export function resolveBookingProductId(slugOrId: string, env: BokunEnv): string | null {
  const m = findBySlugOrProductionId(slugOrId);
  if (env === "sandbox") return m?.testProductId ?? null;
  if (m?.testOnly) return null;
  return m?.productionProductId ?? null;
}

export function isTestOnlySlug(slug: string): boolean {
  return BOKUN_PRODUCT_MAP.some((m) => m.slug === slug && m.testOnly);
}

/** Tour de prueba visible solo con ?mayatest=1 (se recuerda en la pestaña). */
export function isMayaSandboxTestEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search).get("mayatest");
    if (q === "1") window.sessionStorage.setItem("mm:maya-test", "1");
    if (q === "0") window.sessionStorage.removeItem("mm:maya-test");
    return window.sessionStorage.getItem("mm:maya-test") === "1";
  } catch {
    return false;
  }
}
